import Constants, { LoginConsts } from 'client/util/Constants';

const { routeGeneratorError, userConsoleRootRoute } = LoginConsts;
const { admin, user } = LoginConsts.clientType;
const { xsrf } = Constants;

// Constant for RouterOptions object
export const VueRouterMode = 'history';

/**
 * The keys of the legacy routes object is the value of the template parameter.
 * componentName is the name of the Vue component that is rendered for that route.
 * clientType is a prop that will be passed to the vue component to determine api calls.
 */
export const LegacyQueryRoutes = {
  adminLogin: {
    clientType: admin,
    componentName: 'AdminLoginWrapper',
  },

  adminPasswordResetForm: {
    clientType: admin,
    componentName: 'AdminPasswordResetAndSetup',
  },

  adminTOTPResetForm: {
    clientType: admin,
    componentName: 'AdminTOTPReset',
  },

  chooseOS: {
    clientType: user,
    componentName: 'ChooseOS',
  },

  loginMFA: {
    clientType: admin,
    componentName: 'VerifyTOTP',
  },

  loginUserMFA: {
    clientType: user,
    componentName: 'VerifyTOTP',
    csrfType: xsrf,
  },

  oauth: {
    clientType: user,
    componentName: 'OauthLoginWrapper',
    csrfType: xsrf,
  },

  resetMacPassword: {
    componentName: 'MacPasswordReset',
  },

  resetUserPassword: {
    clientType: user,
    componentName: 'RequestPasswordReset',
  },

  resetAdminPassword: {
    clientType: admin,
    componentName: 'RequestPasswordReset',
  },

  resetAdminTOTP: {
    clientType: admin,
    componentName: 'RequestTOTPReset',
  },

  resetPasswordAdminForm: {
    clientType: admin,
    componentName: 'AdminPasswordResetAndSetup',
  },

  resetPasswordAdminMFA: {
    clientType: admin,
    componentName: 'AdminTotpSetup',
  },

  resetPasswordUserForm: {
    clientType: user,
    componentName: 'UserResetPasswordWrapper',
  },

  resetWindowsPassword: {
    componentName: 'WindowsPasswordReset',
  },

  sso: {
    clientType: user,
    componentName: 'SsoLoginWrapper',
    csrfType: xsrf,
  },

  userActivation: {
    clientType: user,
    componentName: 'UserRegistration',
  },

  userLogin: {
    clientType: user,
    componentName: 'UserLoginWrapper',
  },

  default: {
    componentName: 'UserLoginWrapper',
  },

};

/**
 * Gets the window origin. window.origin does not work properly in some versions of microsoft edge.
 * This is added to build that origin url if the browser does not support window.origin or
 * window.location.origin. See getEnv() to see how this is used. See the following link for more
 * details: https://tosbourn.com/a-fix-for-window-location-origin-in-internet-explorer/
 * @return {String} [the origin of the url]
 */
export const getOrigin = () => {
  let origin = window.origin || window.location.origin;
  if (!origin) {
    const { hostname, protocol, port } = window.location;
    origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  return origin;
};

/**
 * Sinon does not allow spies or stubs for standalone exported functions. Therefore, the helper
 * object was created so the validateArgs method can be tested. This is used to validate non
 * optional parameters passed to route generator functions.
 */
export const routeGeneratorHelpers = {
  validateArgs: (...args) => {
    if (!args.length) {
      throw new Error(routeGeneratorError);
    }
    args.forEach((arg) => {
      if (!arg) {
        throw new Error(routeGeneratorError);
      }
    });
  },
  /**
   * Gets the Environment based on the origin of the window.
   * @TODO: this should be done during the build process and injected into webpack
   * @return {String} [the environment that the application is running in]
   */
  getEnv: () => {
    const env = {
      'http://10.0.2.2': 'local',
      'http://0.0.0.0': 'local',
      'http://localhost': 'local',
      'https://console.jumpcloud.local': 'local',
      'https://console.awsstg.jumpcloud.com': 'staging',
      'https://console.jumpcloud.com': 'prod',
    };

    const key = Object.keys(env).filter(((e) => getOrigin().indexOf(e) > -1)).reduce((_, cur) => cur, '');
    return env[key];
  },
};

/**
 * the routeGenerator should be used to keep track of the routes used across
 * the login pages. This was created in order to avoid any potential spelling
 * mistakes when redirecting.
 */
export default {
  adminTotpSetup: '/login/admin/totp',
  chooseMfa: 'choosemfa',
  duo2fa: 'duo2fa',
  googleLogin: '/oauth/google/login',
  login: '/login',
  loginAdmin: '/login/admin',
  loginProviderNotLinked: '/login/provider-not-linked',
  oauth: 'oauth',
  push: 'push',
  signup: '/signup',
  totp: 'totp',
  webauthn: 'webauthn',
};

// Admin Routes
export const AdminRoutes = {
  dashboard: '/',
  logout: '/logout',
  mtp: '/mtp',
  qrCode: '/totp-qr-gen',
  resetMacPassword: '/login?template=resetMacPassword',
  resetPassword: '/login?template=resetAdminPassword',
  resetTOTP: '/login?template=resetAdminTOTP',
  resetUserPassword: '/login?template=resetPasswordUserForm',
  resetWindowsPassword: '/login?template=resetWindowsPassword',
  verifyMFA: '/login?template=loginMFA',
};

// User Routes
export const UserRoutes = {
  dashboard: userConsoleRootRoute,

  /**
   * Route for the MFA verification page. If a redirectTo (SSO) route is provided it returns
   * the same route as the MFA but passes the redirectTo url as a url parameter.
   * @param  {String} redirectTo [optional SSO application route]
   * @return {String}            [MFA route or SSOMFA route]
   */
  mfa(redirectTo) {
    const userMFARoute = 'login?template=loginUserMFA';
    return redirectTo ? `${userMFARoute}&redirectTo=${redirectTo}` : userMFARoute;
  },

  /**
   * Builds the route that will redirect the user to the OAuth endpoint they are trying to
   * access.
   * TODO(cwpinkerton): update if OAuth gets its own domain
   * @param {String} redirectTo [oauth endpoint path the user is attempting to login in to extracted
   *                             from the query params in LoginRouter]
   * @returns {String} [the url to the user MFA page with the redirectTo passed as query params]
   */
  oauthRedirectUrl(redirectTo, getEnv = routeGeneratorHelpers.getEnv) {
    routeGeneratorHelpers.validateArgs(redirectTo);
    const oauthOrigins = {
      local: 'https://console.jumpcloud.local/userconsole/oauth2/',
      staging: 'https://console.awsstg.jumpcloud.com/userconsole/oauth2/',
      prod: 'https://console.jumpcloud.com/userconsole/oauth2/',
    };
    const originByEnv = oauthOrigins[getEnv()];

    return redirectTo.startsWith(originByEnv)
      ? redirectTo
      : `${originByEnv}${redirectTo}`;
  },

  /**
   * Generates the string that will be used to create the data url for the user qr code. The qr code
   * is generated using a client side qr code generator.
   * @param  {String} email [users email]
   * @param  {String} totp  [totp key from the email]
   * @return {String}       [the value that will be passed to the client side qr code generator]
   */
  qrCodeValue(email, totp) {
    routeGeneratorHelpers.validateArgs(email, totp);
    return `otpauth://totp/${email}?secret=${totp}&issuer=JumpCloud%20User`;
  },

  resetPassword: 'login?template=resetUserPassword',

  resetPasswordUserForm: '/login?template=resetPasswordUserForm',

  /**
   * Route for the start of the MFA flow. Will be subject to change when reseting MFA does not rely
   * on reseting their passsword.
   * @param  {String} totpKey [totpKey provided from the reset password email]
   * @param  {String} email   [email of the user]
   * @return {String}         [setup mfa route]
   */
  setupMFA(totpKey, email) {
    routeGeneratorHelpers.validateArgs(totpKey, email);
    return `/login?template=resetPasswordUserMFA&totp=${totpKey}&email=${email}`;
  },

  /**
   * Builds the route that will redirect the user to the application they are trying to access with
   * SSO. Can handle full redirectTo url or application path only
   * (e.g. https://sso.jumpcloud.local/saml2/iamshowcase or saml2/iamshowcase)
   * @param {String} redirectTo [application path the user is attempting to login in to extracted
   *                             from the query params in SsoLoginWrapper]
   * @returns {String} [the url to the user MFA page with the redirectTo passed as query params]
   */
  ssoRedirectUrl(redirectTo, getEnv = routeGeneratorHelpers.getEnv) {
    routeGeneratorHelpers.validateArgs(redirectTo);
    const ssoOrigins = {
      local: 'https://sso.jumpcloud.local/',
      staging: 'https://sso.awsstg.jumpcloud.com/',
      prod: 'https://sso.jumpcloud.com/',
    };
    const originByEnv = ssoOrigins[getEnv()];

    return redirectTo.startsWith(originByEnv)
      ? redirectTo
      : `${originByEnv}${redirectTo}`;
  },

  xsrfUrl: `${userConsoleRootRoute}/xsrf`,

  deviceCert() {
    // TODO: use ConfigService's /ui-settings.json data
    switch (routeGeneratorHelpers.getEnv()) {
      case 'local':
        return 'device-cert.jumpcloud.local';
      case 'staging':
        return 'device-cert.awsstg.jumpcloud.com';
      case 'prod':
      default:
        return 'device-cert.jumpcloud.com';
    }
  },
};
